import React from 'react';
import { useHistory } from 'react-router-dom';
import { IDENTITY_FIRST_ONBOARDING } from '../Environment';
import {
  AutoAwesomeRounded,
  CheckCircleRounded,
  FactCheckRounded,
  FormatQuoteRounded,
  InsightsRounded,
  MenuBookRounded,
  PsychologyRounded,
  RecordVoiceOverRounded,
  TimelineRounded,
  TrackChangesRounded,
  TrendingUpRounded,
  VerifiedUserRounded,
} from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import useViewportMode from '../hooks/useViewportMode';
import heroImage from '../Images/synergist-review-hero.png';

const marketingSiteUrl = 'https://synergistpro.io';
const navItems = [
  { label: 'Home', href: `${marketingSiteUrl}/` },
  { label: 'Platform', href: `${marketingSiteUrl}/#platform` },
  { label: 'How it works', href: `${marketingSiteUrl}/#how-it-works` },
  { label: 'AI copilot', href: `${marketingSiteUrl}/#ai-copilot` },
  { label: 'Why Synergist', href: `${marketingSiteUrl}/#why-synergist` },
  { label: 'Pricing', href: `${marketingSiteUrl}/pricing/` },
  { label: 'About', href: `${marketingSiteUrl}/#about` },
];

const outcomes = [
  {
    icon: <FactCheckRounded />,
    title: 'Role-based consistency',
    body: 'Evaluate the abilities and behaviors that matter for each position—not a one-size-fits-all process.',
  },
  {
    icon: <TimelineRounded />,
    title: 'The full development journey',
    body: 'Carry prior goals, coaching notes, and performance observations forward so one recent event does not define the conversation.',
  },
  {
    icon: <TrendingUpRounded />,
    title: 'Growth people can act on',
    body: 'Translate findings into practical development plans and a clearer path toward stronger performance.',
  },
];

const workflow = [
  {
    step: '01',
    title: 'Define the role',
    body: 'Create a position description and a weighted template of abilities, positive behaviors, and behaviors to improve.',
  },
  {
    step: '02',
    title: 'Capture what happens',
    body: 'Record ongoing feedback, goals, notes, and meaningful events throughout development—not just at review time.',
  },
  {
    step: '03',
    title: 'Review with context',
    body: 'Use structured coaching prompts and a guided AI conversation grounded in role context, history, and unfinished goals.',
  },
  {
    step: '04',
    title: 'Build the next step',
    body: 'Publish a durable record and translate the highest-impact opportunities into a practical development plan.',
  },
];

const platformFeatures = [
  ['Position intelligence', 'Draft and refine position descriptions with organization-aware AI.'],
  ['Performance templates', 'Model the skills, behaviors, weights, and expectations unique to each role.'],
  ['Continuous talent record', 'Keep assessments, events, notes, goals, and development work connected over time.'],
  ['Guided coaching', 'Help supervisors move through the right questions without hunting through disconnected screens.'],
  ['Development planning', 'Focus improvement work on the changes most likely to strengthen overall performance.'],
  ['Career visibility', 'Use role templates to make expectations and progression paths easier to understand.'],
];

const sectionSx = {
  scrollMarginTop: 96,
  py: { xs: 8, md: 12 },
};

const Main = () => {
  const history = useHistory();
  const theme = useTheme();
  const pageRef = React.useRef<HTMLDivElement>(null);
  const { mode, isPhone, isTablet } = useViewportMode();
  const touchLayout = isPhone || isTablet;

  const createAccount = () => history.push(IDENTITY_FIRST_ONBOARDING ? '/Start' : '/CreateAccount');
  const signIn = () => history.push('/Login');
  const navigateToSection = (href: string) => (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    window.history.replaceState(null, '', href);

    if (href === '#home') {
      pageRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    document.getElementById(href.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return <Box
    ref={pageRef}
    data-testid="marketing-page"
    data-viewport-mode={mode}
    sx={{
      height: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
      '@supports (height: 100dvh)': {
        height: {
          xs: 'calc(100dvh - 56px - env(safe-area-inset-top))',
          sm: 'calc(100dvh - 64px - env(safe-area-inset-top))',
        },
      },
      overflowY: 'auto',
      overflowX: 'hidden',
      scrollBehavior: 'smooth',
      scrollPaddingTop: touchLayout ? 60 : 66,
      background: theme.palette.mode === 'dark'
        ? `linear-gradient(180deg, ${theme.palette.grey[900]} 0%, #071426 46%, ${theme.palette.grey[900]} 100%)`
        : 'linear-gradient(180deg, #f7faff 0%, #edf5ff 46%, #f7faff 100%)',
    }}>
    <Box
      component="nav"
      aria-label="Marketing navigation"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: theme.zIndex.appBar - 1,
        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
        backgroundColor: alpha(theme.palette.background.paper, 0.9),
        backdropFilter: 'blur(18px)',
      }}>
      <Container maxWidth="xl">
        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            minHeight: touchLayout ? 52 : 58,
            alignItems: 'center',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}>
          {navItems.map(item => <Button
            key={item.href}
            component="a"
            href={item.href}
            onClick={item.href.startsWith('#') ? navigateToSection(item.href) : undefined}
            color="inherit"
            size="small"
            sx={{
              flexShrink: 0,
              minHeight: touchLayout ? 44 : 36,
              px: { xs: 1.25, sm: 2 },
              fontWeight: 700,
              color: 'text.secondary',
            }}>
            {item.label}
          </Button>)}
        </Stack>
      </Container>
    </Box>

    <Box
      component="section"
      id="home"
      aria-labelledby="marketing-hero-title"
      sx={{
        position: 'relative',
        minHeight: { xs: 880, sm: 720, lg: 700 },
        display: 'flex',
        alignItems: 'center',
        color: 'common.white',
        backgroundColor: '#041226',
        backgroundImage: {
          xs: `linear-gradient(180deg, rgba(2,12,29,.99) 0%, rgba(2,12,29,.98) 62%, rgba(2,12,29,.78) 80%, rgba(2,12,29,.38) 100%), url(${heroImage})`,
          sm: `linear-gradient(90deg, rgba(2,12,29,.98) 0%, rgba(2,12,29,.92) 34%, rgba(2,12,29,.35) 63%, rgba(2,12,29,.12) 100%), url(${heroImage})`,
        },
        backgroundSize: { xs: 'cover, auto 52%', sm: 'cover, cover', lg: 'cover, min(1662px, 100vw) auto' },
        backgroundPosition: { xs: 'center, center bottom', sm: 'center, center', lg: 'center, center right' },
        backgroundRepeat: 'no-repeat',
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: { xs: 'linear-gradient(180deg, transparent 58%, rgba(2,12,29,.28) 100%)', sm: 'none' },
        },
      }}>
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, py: { xs: 7, sm: 9, lg: 12 }, alignSelf: { xs: 'flex-start', sm: 'center' } }}>
        <Box sx={{ width: { xs: '100%', sm: '64%', lg: '60%' }, maxWidth: 760 }}>
          <Chip
            icon={<AutoAwesomeRounded />}
            label="Talent development, built around role clarity"
            sx={{
              mb: 3,
              minHeight: 36,
              color: 'common.white',
              border: `1px solid ${alpha('#7ce7d7', 0.55)}`,
              backgroundColor: alpha('#1fb9ad', 0.16),
              '& .MuiChip-icon': { color: '#7ce7d7' },
            }}
          />
          <Typography
            id="marketing-hero-title"
            component="h1"
            sx={{
              fontWeight: 900,
              letterSpacing: '-0.045em',
              lineHeight: 0.98,
              fontSize: { xs: '2.85rem', sm: '3.55rem', lg: '4.1rem' },
              textWrap: 'balance',
              textShadow: '0 12px 42px rgba(0,0,0,.32)',
            }}>
            Clearer development.<br />Fairer decisions.<br />Stronger teams.
          </Typography>
          <Typography sx={{ mt: 3, maxWidth: 630, fontSize: { xs: '1.08rem', sm: '1.25rem' }, lineHeight: 1.65, color: alpha('#fff', 0.82) }}>
            Synergist Pro replaces vague annual evaluations with role-specific expectations, continuous context, and guided coaching conversations that help teams make fairer, more consistent development decisions.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4, maxWidth: { xs: 440, sm: 'none' } }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={createAccount}
              sx={{ minHeight: 52, px: 3.5, fontWeight: 800, boxShadow: '0 16px 36px rgba(47,123,255,.38)' }}>
              Create your organization
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              sx={{ minHeight: 52, px: 3, color: 'common.white', borderColor: alpha('#fff', 0.45), '&:hover': { borderColor: '#fff', backgroundColor: alpha('#fff', 0.08) } }}>
              See how it works
            </Button>
          </Stack>
          <Stack direction="row" flexWrap="wrap" gap={2.25} sx={{ mt: 4, color: alpha('#fff', 0.76) }}>
            {['Role-specific', 'Evidence-informed', 'Supervisor-approved'].map(label => <Stack key={label} direction="row" spacing={0.75} alignItems="center">
              <CheckCircleRounded sx={{ color: '#7ce7d7', fontSize: 19 }} />
              <Typography variant="body2" fontWeight={700}>{label}</Typography>
            </Stack>)}
          </Stack>
        </Box>
      </Container>
    </Box>

    <Box component="section" id="why-synergist" sx={{ ...sectionSx, pt: { xs: 7, md: 9 } }}>
      <Container maxWidth="lg">
        <Stack spacing={2} sx={{ maxWidth: 820, mb: 6 }}>
          <Typography color="primary.main" fontWeight={900} textTransform="uppercase" letterSpacing=".12em" variant="overline">A better foundation for performance</Typography>
          <Typography component="h2" variant={isPhone ? 'h3' : 'h2'} fontWeight={900} letterSpacing="-.035em">
            Move beyond memory, mood, and recency.
          </Typography>
            <Typography variant="h6" color="text.secondary" lineHeight={1.65} fontWeight={400}>
              Traditional check-ins ask supervisors to summarize months of work from memory. Synergist Pro anchors the conversation in explicit role expectations and longitudinal context, helping organizations make more consistent, explainable decisions.
            </Typography>
        </Stack>
        <Grid container spacing={3}>
          {outcomes.map(item => <Grid item xs={12} md={4} key={item.title}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 4, background: alpha(theme.palette.background.paper, 0.8), borderColor: alpha(theme.palette.primary.main, 0.16) }}>
              <CardContent sx={{ p: { xs: 3, md: 4 }, '&:last-child': { pb: { xs: 3, md: 4 } } }}>
                <Box sx={{ width: 52, height: 52, display: 'grid', placeItems: 'center', borderRadius: 3, color: 'primary.main', backgroundColor: alpha(theme.palette.primary.main, 0.12), mb: 3 }}>
                  {item.icon}
                </Box>
                <Typography component="h3" variant="h5" fontWeight={850}>{item.title}</Typography>
                <Typography color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.7 }}>{item.body}</Typography>
              </CardContent>
            </Card>
          </Grid>)}
        </Grid>
      </Container>
    </Box>

    <Box component="section" id="how-it-works" sx={{ ...sectionSx, backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.045) }}>
      <Container maxWidth="lg">
        <Stack alignItems="center" textAlign="center" spacing={2} sx={{ maxWidth: 800, mx: 'auto', mb: 7 }}>
          <Typography color="secondary.main" fontWeight={900} textTransform="uppercase" letterSpacing=".12em" variant="overline">How it works</Typography>
            <Typography component="h2" variant={isPhone ? 'h3' : 'h2'} fontWeight={900} letterSpacing="-.035em">From role clarity to real development.</Typography>
          <Typography variant="h6" color="text.secondary" fontWeight={400} lineHeight={1.65}>One connected workflow keeps coaching and growth work grounded, efficient, and useful long after meetings end.</Typography>
        </Stack>
        <Grid container spacing={3}>
          {workflow.map((item, index) => <Grid item xs={12} sm={6} lg={3} key={item.step}>
            <Paper variant="outlined" sx={{ p: 3, height: '100%', borderRadius: 4, position: 'relative', overflow: 'hidden' }}>
              <Typography aria-hidden="true" sx={{ position: 'absolute', right: 16, top: -8, fontSize: '5rem', fontWeight: 900, color: alpha(theme.palette.primary.main, 0.08), lineHeight: 1 }}>{item.step}</Typography>
              <Chip label={`Step ${index + 1}`} color={index % 2 === 0 ? 'primary' : 'secondary'} size="small" sx={{ mb: 3, fontWeight: 800 }} />
              <Typography component="h3" variant="h5" fontWeight={850}>{item.title}</Typography>
              <Typography color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.7 }}>{item.body}</Typography>
            </Paper>
          </Grid>)}
        </Grid>
      </Container>
    </Box>

    <Box component="section" id="ai-copilot" sx={sectionSx}>
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <Chip icon={<PsychologyRounded />} label="Conversational coaching copilot" color="primary" variant="outlined" sx={{ mb: 3, fontWeight: 800 }} />
            <Typography component="h2" variant={isPhone ? 'h3' : 'h2'} fontWeight={900} letterSpacing="-.035em">AI that assists. People who decide.</Typography>
            <Typography variant="h6" color="text.secondary" fontWeight={400} lineHeight={1.7} sx={{ mt: 2 }}>
              Supervisors can talk or type through a coaching session while the copilot asks relevant questions and proposes ratings, notes, and development goals from the employee’s template and history.
            </Typography>
            <Stack spacing={2.25} sx={{ mt: 4 }}>
              {[
                ['Individual approval', 'Every suggested change stays pending until the supervisor explicitly accepts or rejects it.'],
                ['Built-in accountability', 'Accepted, rejected, and undone proposals retain an audit trail tied to the whole development process.'],
                ['Private by design', 'The supervisor conversation stays supervisor-privileged; the employee receives the finalized development record.'],
              ].map(([title, body]) => <Stack key={title} direction="row" spacing={2} alignItems="flex-start">
                <VerifiedUserRounded color="secondary" sx={{ mt: 0.25 }} />
                <Box><Typography fontWeight={850}>{title}</Typography><Typography color="text.secondary" lineHeight={1.6}>{body}</Typography></Box>
              </Stack>)}
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ borderRadius: 5, overflow: 'hidden', border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, background: `linear-gradient(145deg, ${alpha(theme.palette.primary.dark, 0.96)}, ${alpha(theme.palette.secondary.dark, 0.94)})`, color: 'common.white', p: { xs: 3, sm: 4.5 }, boxShadow: '0 30px 80px rgba(4,18,38,.28)' }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: alpha('#fff', 0.12), display: 'grid', placeItems: 'center' }}><RecordVoiceOverRounded /></Box>
                <Box><Typography fontWeight={900}>Synergist AI</Typography><Typography variant="body2" sx={{ color: alpha('#fff', 0.7) }}>Coaching conversation</Typography></Box>
              </Stack>
              <Divider sx={{ my: 3, borderColor: alpha('#fff', 0.14) }} />
              <Typography sx={{ color: alpha('#fff', 0.68), fontSize: '.78rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em' }}>A grounded question</Typography>
              <Typography variant="h5" fontWeight={750} lineHeight={1.45} sx={{ mt: 1 }}>
                “What evidence from this development period best reflects how the teammate handled changing priorities?”
              </Typography>
              <Paper sx={{ mt: 3, p: 2.5, borderRadius: 3, backgroundColor: alpha('#fff', 0.09), color: 'inherit', border: `1px solid ${alpha('#fff', 0.12)}` }}>
                <Stack direction="row" spacing={1.25} alignItems="center"><TrackChangesRounded sx={{ color: '#7ce7d7' }} /><Typography fontWeight={850}>Proposal ready for review</Typography></Stack>
                <Typography variant="body2" sx={{ mt: 1, color: alpha('#fff', 0.72), lineHeight: 1.6 }}>The supervisor sees the rationale and decides whether it belongs in the draft.</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}><Chip label="Accept" size="small" sx={{ color: '#fff', backgroundColor: alpha('#22c55e', 0.4) }} /><Chip label="Reject" size="small" sx={{ color: '#fff', backgroundColor: alpha('#fff', 0.1) }} /></Stack>
              </Paper>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>

    <Box component="section" id="platform" sx={{ ...sectionSx, backgroundColor: alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.07 : 0.045) }}>
      <Container maxWidth="lg">
        <Stack spacing={2} sx={{ maxWidth: 790, mb: 6 }}>
          <Typography color="primary.main" fontWeight={900} textTransform="uppercase" letterSpacing=".12em" variant="overline">One connected platform</Typography>
          <Typography component="h2" variant={isPhone ? 'h3' : 'h2'} fontWeight={900} letterSpacing="-.035em">Everything around development stays connected.</Typography>
          <Typography variant="h6" color="text.secondary" fontWeight={400} lineHeight={1.65}>Build expectations, capture performance evidence, run coaching sessions, and guide growth without stitching together a trail of forms and spreadsheets.</Typography>
        </Stack>
        <Grid container spacing={2.5}>
          {platformFeatures.map(([title, body], index) => <Grid item xs={12} sm={6} md={4} key={title}>
            <Stack direction="row" spacing={2} sx={{ p: 2.5, height: '100%', borderRadius: 3, backgroundColor: alpha(theme.palette.background.paper, 0.72), border: `1px solid ${alpha(theme.palette.divider, 0.8)}` }}>
              <Box sx={{ flexShrink: 0, width: 38, height: 38, display: 'grid', placeItems: 'center', borderRadius: 2.5, color: index % 2 ? 'secondary.main' : 'primary.main', backgroundColor: alpha(index % 2 ? theme.palette.secondary.main : theme.palette.primary.main, 0.12) }}>
                {index % 3 === 0 ? <MenuBookRounded fontSize="small" /> : index % 3 === 1 ? <InsightsRounded fontSize="small" /> : <CheckCircleRounded fontSize="small" />}
              </Box>
              <Box><Typography fontWeight={850}>{title}</Typography><Typography variant="body2" color="text.secondary" lineHeight={1.6} sx={{ mt: 0.5 }}>{body}</Typography></Box>
            </Stack>
          </Grid>)}
        </Grid>
      </Container>
    </Box>

    <Box component="section" id="about" sx={sectionSx}>
      <Container maxWidth="md">
        <Stack alignItems="center" textAlign="center" spacing={3}>
          <FormatQuoteRounded sx={{ fontSize: 54, color: 'secondary.main' }} />
          <Typography component="h2" variant={isPhone ? 'h3' : 'h2'} fontWeight={900} letterSpacing="-.035em">Performance management should develop people—not just document them.</Typography>
          <Typography variant="h6" color="text.secondary" lineHeight={1.75} fontWeight={400}>
            Synergist Pro was designed around a simple idea: people deserve clear expectations, fair evaluation, useful feedback, and a practical path forward. The platform gives supervisors structure without taking away judgment—and gives organizations a more durable view of how their people grow.
          </Typography>
        </Stack>
      </Container>
    </Box>

    <Container maxWidth="lg" sx={{ pb: { xs: 8, md: 12 } }}>
      <Paper sx={{ overflow: 'hidden', position: 'relative', borderRadius: 5, p: { xs: 3.5, sm: 6, md: 8 }, color: 'common.white', background: 'linear-gradient(125deg, #0b318f 0%, #1a56d6 52%, #12857e 100%)', boxShadow: '0 28px 90px rgba(11,49,143,.28)' }}>
        <Box sx={{ position: 'absolute', width: 420, height: 420, right: -160, top: -230, borderRadius: '50%', backgroundColor: alpha('#fff', 0.09) }} />
        <Box sx={{ position: 'relative', maxWidth: 760 }}>
          <Typography component="h2" variant={isPhone ? 'h3' : 'h2'} fontWeight={900} letterSpacing="-.035em">Make the next development conversation easier—and more meaningful.</Typography>
          <Typography variant="h6" sx={{ mt: 2, color: alpha('#fff', 0.78), lineHeight: 1.65, fontWeight: 400 }}>Start with role clarity today, then build a coaching system your supervisors and teammates can actually use.</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4 }}>
            <Button variant="contained" color="secondary" size="large" onClick={createAccount} sx={{ minHeight: 52, px: 3.5, fontWeight: 850 }}>Create an account</Button>
            <Button variant="text" size="large" onClick={signIn} sx={{ minHeight: 52, px: 3, color: 'common.white', fontWeight: 800 }}>Already use Synergist Pro? Sign in</Button>
          </Stack>
        </Box>
      </Paper>
    </Container>

    <Box component="footer" sx={{ borderTop: `1px solid ${theme.palette.divider}`, backgroundColor: alpha(theme.palette.background.paper, 0.68) }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
          <Box><Typography fontWeight={900}>Synergist Pro</Typography><Typography variant="body2" color="text.secondary">Coaching clarity. Focused development. Stronger teams.</Typography></Box>
          <Stack direction="row" flexWrap="wrap" gap={2.5}>{navItems.map(item => <Link key={item.href} href={item.href} onClick={item.href.startsWith('#') ? navigateToSection(item.href) : undefined} color="text.secondary" underline="hover" fontWeight={700}>{item.label}</Link>)}</Stack>
        </Stack>
      </Container>
    </Box>
  </Box>;
};

export default Main;
